import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Search, UserPlus, Loader2, Check, ClipboardList, AlertTriangle, Users,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button } from "../../ui";
import { getContacts } from "../../../api/contact";
import { addPropertyBuyer, addPropertyBuyersBulk } from "../../../api/propertyManagement";
import toast from "react-hot-toast";

/**
 * Attach buyers to a property's "Buyers" tab.
 *
 * Three modes:
 *  - "search": find existing CRM contacts and attach them. Tick as many as you
 *    like and add them in one action. An agent who had thirty buyers through a
 *    listing should not have to repeat a one-at-a-time flow thirty times
 *    (Christian Gravias, Monday DOMA 12840979019).
 *  - "paste":  paste a list (one buyer per line, name/email/phone in any order).
 *    Anyone already in the CRM is matched on email then phone; the rest are
 *    created. A line with no email and no phone is reported back, not guessed at.
 *  - "create": quick-create a single brand-new buyer contact.
 *
 * Attaching a buyer folds this listing's specs (suburb, price, beds, baths, type)
 * into their Buying Preferences, widening what they already had rather than
 * replacing it, which is what makes them appear in this property's Buyer Match.
 * The checkbox lets an agent opt out for a buyer they know is unrelated.
 *
 * After a successful attach, calls onAdded() so the parent can refresh its list.
 */
export default function AddBuyerModal({ isOpen, onClose, propertyId, existingContactIds = [], onAdded, isLease = false }) {
  const personLabel = isLease ? "renter" : "buyer";
  const personLabelCap = isLease ? "Renter" : "Buyer";
  const personLabelPlural = isLease ? "renters" : "buyers";
  const personLabelPluralCap = isLease ? "Renters" : "Buyers";
  const matchLabel = isLease ? "Renter Match" : "Buyer Match";
  const prefsLabel = isLease ? "renting preferences" : "buying preferences";
  const [mode, setMode] = useState("search");

  // Search mode
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState([]); // [{id, name, email, phone}], survives re-searching
  const debounceRef = useRef(null);

  // Paste mode
  const [pasted, setPasted] = useState("");

  // Create mode
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  // Shared
  const [notes, setNotes] = useState("");
  const [seedPrefs, setSeedPrefs] = useState(true);
  const [submittingId, setSubmittingId] = useState(null); // contact id, 'new', or 'bulk'
  const [skipped, setSkipped] = useState([]);             // rows the server couldn't add

  const existing = useMemo(
    () => new Set((existingContactIds || []).map(String)),
    [existingContactIds],
  );
  const pickedIds = useMemo(() => new Set(picked.map((p) => String(p.id))), [picked]);

  // Reset everything whenever the modal opens.
  useEffect(() => {
    if (isOpen) {
      setMode("search");
      setQuery("");
      setResults([]);
      setPicked([]);
      setPasted("");
      setForm({ name: "", email: "", phone: "" });
      setNotes("");
      setSeedPrefs(true);
      setSubmittingId(null);
      setSkipped([]);
    }
  }, [isOpen]);

  // Debounced contact search.
  useEffect(() => {
    if (!isOpen || mode !== "search") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await getContacts({ search: q });
        const list = res?.data?.results || res?.data || [];
        setResults(Array.isArray(list) ? list.slice(0, 25) : []);
      } catch (e) {
        console.error("Contact search failed:", e);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, isOpen, mode]);

  // Ticking a row keeps it selected even after the search box is retyped, so an
  // agent can gather buyers across several searches and add them all at once.
  const togglePick = (contact) => {
    // "Virtual" rows from the contact list (e.g. unlinked enquiries) have
    // non-numeric ids and aren't real Contact records yet, so prefill the create
    // form with their details instead of attempting an invalid link.
    if (!/^\d+$/.test(String(contact.id))) {
      setForm({ name: contact.name || "", email: contact.email || "", phone: contact.phone || "" });
      setMode("create");
      toast(`This person isn't a saved contact yet, so adding them as a new ${personLabel}.`, { icon: "ℹ️" });
      return;
    }
    setPicked((prev) =>
      prev.some((p) => String(p.id) === String(contact.id))
        ? prev.filter((p) => String(p.id) !== String(contact.id))
        : [...prev, { id: contact.id, name: contact.name, email: contact.email, phone: contact.phone }],
    );
  };

  const selectAllResults = () => {
    const addable = results.filter(
      (c) => /^\d+$/.test(String(c.id)) && !existing.has(String(c.id)) && !pickedIds.has(String(c.id)),
    );
    if (!addable.length) return;
    setPicked((prev) => [
      ...prev,
      ...addable.map((c) => ({ id: c.id, name: c.name, email: c.email, phone: c.phone })),
    ]);
  };

  // How many people the paste box describes, shown live so the agent can sanity
  // check the count before committing 200 rows.
  const pastedCount = useMemo(
    () => pasted.split("\n").filter((l) => l.trim()).length,
    [pasted],
  );

  const reportBulk = (data) => {
    const rows = data?.skipped || [];
    setSkipped(rows);
    if (data?.attached || data?.already_attached) toast.success(data.detail || `${personLabelPluralCap} added.`);
    else toast(data?.detail || "Nothing to add.", { icon: "ℹ️" });
    onAdded?.();
    // Leave the modal open when something was skipped so the agent actually sees
    // which rows need attention; a clean run closes as before.
    if (!rows.length) onClose();
  };

  const handleAddPicked = async () => {
    if (!picked.length) {
      toast.error("Tick at least one contact.");
      return;
    }
    setSubmittingId("bulk");
    setSkipped([]);
    try {
      const res = await addPropertyBuyersBulk(propertyId, {
        contact_ids: picked.map((p) => p.id),
        notes: notes.trim(),
        seed_preferences: seedPrefs,
      });
      setPicked([]);
      reportBulk(res.data);
    } catch (e) {
      console.error("Failed to add buyers:", e);
      toast.error(e?.response?.data?.error || `Could not add those ${personLabelPlural}.`);
    } finally {
      setSubmittingId(null);
    }
  };

  const handleAddPasted = async () => {
    if (!pasted.trim()) {
      toast.error("Paste a list first.");
      return;
    }
    setSubmittingId("bulk");
    setSkipped([]);
    try {
      const res = await addPropertyBuyersBulk(propertyId, {
        paste: pasted,
        notes: notes.trim(),
        seed_preferences: seedPrefs,
      });
      setPasted("");
      reportBulk(res.data);
    } catch (e) {
      console.error("Failed to add pasted buyers:", e);
      toast.error(e?.response?.data?.error || "Could not add that list.");
    } finally {
      setSubmittingId(null);
    }
  };

  const handleCreateNew = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter a name.");
      return;
    }
    setSubmittingId("new");
    try {
      await addPropertyBuyer(propertyId, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notes: notes.trim(),
      });
      toast.success(`${form.name.trim()} added to this property.`);
      onAdded?.();
      onClose();
    } catch (e) {
      console.error("Failed to create buyer:", e);
      toast.error(e?.response?.data?.error || `Could not add ${personLabel}.`);
    } finally {
      setSubmittingId(null);
    }
  };

  const inputCls =
    "h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#55C5D0]";
  const busy = submittingId === "bulk";

  const TabButton = ({ id, label }) => (
    <button
      type="button"
      onClick={() => setMode(id)}
      className={`flex-1 py-1.5 rounded-md transition-colors ${mode === id ? "bg-white text-[#3a99a3] shadow-sm" : "text-gray-500"}`}
    >
      {label}
    </button>
  );

  const NoteField = (
    <div>
      <label className="text-xs font-bold text-[#374151] block mb-1">Note (optional)</label>
      <input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="e.g. Came through the 5 Jun open home"
        className={inputCls}
      />
    </div>
  );

  const SeedToggle = (
    <label className="flex items-start gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={seedPrefs}
        onChange={(e) => setSeedPrefs(e.target.checked)}
        className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0]"
      />
      <span className="text-[11px] leading-snug text-gray-500">
        Add this listing&apos;s details (suburb, {isLease ? "rent" : "price"}, beds, baths, type) to each
        {" "}{personLabel}&apos;s {prefsLabel}, so they show up in {matchLabel}. Widens what
        they already had, nothing they told us is overwritten.
      </span>
    </label>
  );

  const SkippedPanel = skipped.length ? (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1.5">
      <p className="text-xs font-bold text-amber-800 flex items-center gap-1.5">
        <AlertTriangle size={13} /> {skipped.length} couldn&apos;t be added
      </p>
      <ul className="max-h-28 overflow-y-auto text-[11px] text-amber-800 space-y-0.5">
        {skipped.map((s, i) => (
          <li key={`${s.input}-${i}`} className="truncate">
            <span className="font-semibold">{s.input || "(blank)"}</span>: {s.reason}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={18} className="text-[#55C5D0]" /> Add {personLabelPluralCap} to Property
          </DialogTitle>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg text-sm font-semibold">
          <TabButton id="search" label="Existing contacts" />
          <TabButton id="paste" label="Paste a list" />
          <TabButton id="create" label={`New ${personLabel}`} />
        </div>

        {mode === "search" ? (
          <div className="py-2 space-y-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email or phone…"
                className={`${inputCls} pl-9`}
              />
            </div>

            {picked.length > 0 && (
              <div className="flex items-start gap-2 rounded-lg bg-[#F0FDFE] border border-[#B7E9EE] p-2.5">
                <Users size={14} className="text-[#3a99a3] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-[#3a99a3]">
                    {picked.length} selected
                  </p>
                  <p className="text-[11px] text-gray-500 truncate">
                    {picked.map((p) => p.name || p.email || p.phone || `#${p.id}`).join(", ")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPicked([])}
                  className="text-[11px] font-bold text-gray-400 hover:text-gray-600 shrink-0"
                >
                  Clear
                </button>
              </div>
            )}

            <div className="max-h-[240px] overflow-y-auto -mx-1 px-1">
              {searching ? (
                <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
                  <Loader2 size={16} className="animate-spin mr-2" /> Searching…
                </div>
              ) : query.trim().length < 2 ? (
                <p className="text-center text-xs text-gray-400 py-8 uppercase tracking-widest font-bold">
                  Type at least 2 characters to search
                </p>
              ) : results.length === 0 ? (
                <div className="text-center py-8 space-y-3">
                  <p className="text-sm text-gray-500">No contacts found for “{query.trim()}”.</p>
                  <button
                    type="button"
                    onClick={() => { setForm((f) => ({ ...f, name: query.trim() })); setMode("create"); }}
                    className="text-xs font-bold text-[#3a99a3] hover:underline"
                  >
                    + Create a new {personLabel} instead
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex justify-end pb-1">
                    <button
                      type="button"
                      onClick={selectAllResults}
                      className="text-[11px] font-bold text-[#3a99a3] hover:underline"
                    >
                      Select all {results.length} results
                    </button>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {results.map((c) => {
                      const already = existing.has(String(c.id));
                      const isPicked = pickedIds.has(String(c.id));
                      return (
                        <button
                          key={c.id}
                          type="button"
                          disabled={already || busy}
                          onClick={() => togglePick(c)}
                          className="w-full text-left py-2.5 px-2 flex items-center gap-3 hover:bg-gray-50 rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <span
                            className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                              already
                                ? "bg-emerald-500 border-emerald-500"
                                : isPicked
                                  ? "bg-[#55C5D0] border-[#55C5D0]"
                                  : "border-gray-300"
                            }`}
                          >
                            {(already || isPicked) && <Check size={11} className="text-white" />}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold text-[#374151] truncate">{c.name || "(no name)"}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {[c.email, c.phone].filter(Boolean).join(" · ") || "No contact details"}
                            </p>
                          </div>
                          {already && (
                            <span className="shrink-0 text-[11px] font-bold text-emerald-600">Added</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {NoteField}
            {SeedToggle}
            {SkippedPanel}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAddPicked}
                disabled={busy || picked.length === 0}
                className="bg-[#55C5D0] hover:bg-[#4AADB7] text-white"
              >
                {busy ? (
                  <><Loader2 size={14} className="animate-spin mr-1.5" /> Adding…</>
                ) : (
                  `Add ${picked.length || ""} ${picked.length === 1 ? personLabelCap : personLabelPluralCap}`.replace("  ", " ")
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : mode === "paste" ? (
          <div className="py-2 space-y-3">
            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">
                Paste your {personLabel} list, one person per line
              </label>
              <textarea
                autoFocus
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                rows={8}
                spellCheck={false}
                placeholder={
                  "Jane Smith, jane@example.com, 0400 111 222\n" +
                  "bob@example.com\n" +
                  "Tom Jones 0422 333 444"
                }
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm font-mono shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#55C5D0]"
              />
              <p className="text-[11px] text-gray-400 mt-1.5 flex items-start gap-1.5">
                <ClipboardList size={12} className="mt-0.5 shrink-0" />
                Name, email and phone can be in any order, separated by commas or
                tabs. Paste a column straight out of a spreadsheet. Anyone already
                in the CRM is matched on their email or phone rather than duplicated.
                A line with no email and no phone can&apos;t be matched, so it is
                listed back to you instead of guessed at.
              </p>
            </div>

            {pastedCount > 0 && (
              <p className="text-xs font-bold text-[#3a99a3]">
                {pastedCount} {pastedCount === 1 ? "line" : "lines"} to add
              </p>
            )}

            {NoteField}
            {SeedToggle}
            {SkippedPanel}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAddPasted}
                disabled={busy || !pasted.trim()}
                className="bg-[#55C5D0] hover:bg-[#4AADB7] text-white"
              >
                {busy ? (
                  <><Loader2 size={14} className="animate-spin mr-1.5" /> Adding…</>
                ) : (
                  `Add ${pastedCount || ""} ${pastedCount === 1 ? personLabelCap : personLabelPluralCap}`.replace("  ", " ")
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleCreateNew} className="py-2 space-y-3">
            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">Name *</label>
              <input
                autoFocus
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Email address"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">Phone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="Phone number"
                className={inputCls}
              />
            </div>
            {NoteField}
            <p className="text-[11px] text-gray-400">
              Creates a {personLabel} contact and attaches them here. Open their profile afterwards to add
              notes &amp; reminders.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={submittingId === "new"}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#55C5D0] hover:bg-[#4AADB7] text-white" disabled={submittingId === "new"}>
                {submittingId === "new" ? "Adding…" : `Add ${personLabelCap}`}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
